/**
 * CORE-Y (Core-100 R4 W4): the Command Center says how a ticket came to exist — the reader of the recorded-once
 * `tickets.creation_method`. Its own small read (RLS applies); a failed read says so instead of guessing an origin.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ticketOriginLabel } from '@/lib/tickets/ticketOrigin';

type Reader = {
  from: (t: string) => { select: (c: string) => { eq: (k: string, v: string) => { maybeSingle: () => PromiseLike<{ data: { creation_method: string | null } | null; error: unknown }> } } };
};

export function TicketOriginLine({ ticketId }: { ticketId: string }) {
  const [state, setState] = useState<{ status: 'loading' | 'ok' | 'failed'; method?: string | null }>({ status: 'loading' });

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data, error } = await (supabase as unknown as Reader)
          .from('tickets').select('creation_method').eq('id', ticketId).maybeSingle();
        if (!alive) return;
        if (error || !data) setState({ status: 'failed' });
        else setState({ status: 'ok', method: data.creation_method });
      } catch {
        if (alive) setState({ status: 'failed' });
      }
    })();
    return () => { alive = false; };
  }, [ticketId]);

  if (state.status === 'loading') return null;
  return (
    <p className="text-xs text-muted-foreground" data-testid="ticket-origin" data-origin={state.status === 'ok' ? (state.method ?? '') : 'unavailable'}>
      {state.status === 'ok' ? ticketOriginLabel(state.method) : 'Origin could not be read just now.'}
    </p>
  );
}
